import React, { Fragment } from "react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarShortcut,
  MenubarTrigger,
} from "@components/common/menu-bar";
import { Check, Ellipsis } from "lucide-react";
import { Separator } from "../common/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip";
import useTranslator from "@pma/hooks/use-translator";
import { DictType } from "@pma/types";
import { cn } from "@pma/utils/helpers";
import { TabType, useTabsStore } from "@pma/stores/tabs-store";
import { toast } from "@pma/hooks/use-toast";

interface Props {
  options: DictType[];
  context: TabType;
}

const TabViewSettings = ({ options, context }: Props) => {
  const { translate, locale } = useTranslator();
  const { addTab, removeTab, tabs } = useTabsStore();

  const activeTabs = (tabs[`${context}`] || []) as string[];

  const hasStateTabs = activeTabs?.length > 0;

  const handleOptionChange = (option: string) => {
    if (activeTabs?.includes(option)) {
      if (activeTabs.length === 1) {
        return toast({
          title: translate("Info"),
          description: translate("You cannot hide all tabs"),
          variant: "info",
        });
      }
      removeTab(context, option);
    } else {
      addTab(context, option);
    }
  };

  return (
    <div>
      <Menubar className='border-none bg-transparent'>
        <MenubarMenu>
          <Tooltip>
            <TooltipTrigger>
              <MenubarTrigger
                className={cn(
                  "flex items-center justify-center p-0 h-5 w-5 rounded-full cursor-pointer border-none hover:border",
                  "text-black bg-transparent hover:bg-primary hover:text-white",
                )}
              >
                <Ellipsis size={16} />
              </MenubarTrigger>
            </TooltipTrigger>
            <TooltipContent>{translate("Tab settings")}</TooltipContent>
          </Tooltip>
          <MenubarContent className='mx-2 bg-background gap-2'>
            {options.map((option, index) => (
              <Fragment key={index}>
                <MenubarItem
                  key={index}
                  disabled={option?.disabled}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOptionChange(option?.label);
                  }}
                  className='flex items-center text-xs p-1'
                >
                  {translate(option?.label)}
                  <MenubarShortcut>
                    {(activeTabs?.includes(option?.label) || !hasStateTabs) && <Check size={14} />}
                  </MenubarShortcut>
                </MenubarItem>
                <Separator />
              </Fragment>
            ))}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  );
};

export default TabViewSettings;
